"use client";

import { Time } from "@commerce/components/time";
import { NoMatches } from "@commerce/conversations/EmptyStates";
import { orpc, orpcClient } from "@shared/lib/orpc";
import { useDebouncedValue } from "@tanstack/react-pacer";
import {
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@ui/components/input-group";
import { LinkButton } from "@ui/components/link-button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import {
	BellIcon,
	BellOffIcon,
	CircleAlertIcon,
	MapPinIcon,
	PackageIcon,
	SearchIcon,
	SettingsIcon,
	XIcon,
} from "lucide-react";
import {
	useCallback,
	useDeferredValue,
	useEffect,
	useEffectEvent,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	COUNTED_QUEUES,
	listInputFrom,
	money,
	ORDER_QUEUES,
	type OrdersSearch,
	orderName,
	PAYMENT_LABEL,
	QUEUE_LABEL,
	STATUS_META,
} from "./meta";
import {
	OrderDetail,
	OrderDetailEmpty,
	OrderDetailSkeleton,
	WaitingFor,
} from "./OrderDetail";
import type { OrderList as ListData, OrderQueue, OrderRow } from "./types";

/** Fallback only: the org layout's live subscription invalidates on push. */
const HEARTBEAT_POLL_MS = 60_000;
const ALL_BRANCHES = "all";
const SOUND_KEY = "hephon:new-order-sound";

const route = getRouteApi("/_saas/app/_org/$organizationSlug/orders/");

/** Each queue tab's width in px, for the skeleton. */
const QUEUE_TAB_WIDTH: Record<OrderQueue, number> = {
	new: 56,
	preparing: 88,
	on_the_way: 88,
	done: 56,
	cancelled: 88,
};

type ListInput = ReturnType<typeof listInputFrom>;

/**
 * Orders: [queue tabs] [list] [detail], like the Inbox (spec 5.3). Queue, branch, search and
 * the open order live in the URL. Below lg the list and the order are one pane each, the
 * order with a back button and its main action in a sticky bar.
 */
export function OrdersScreen({
	organizationId,
	isAdmin,
}: {
	organizationId: string;
	isAdmin: boolean;
}) {
	const { organizationSlug } = route.useParams();
	const search = route.useSearch();
	const navigate = route.useNavigate();

	const setSearch = useCallback(
		(patch: Partial<OrdersSearch>) =>
			navigate({
				search: (prev) => ({ ...prev, ...patch }),
				replace: true,
			}),
		[navigate],
	);

	const [searchText, setSearchText] = useState(search.q ?? "");
	const [debouncedText] = useDebouncedValue(searchText.trim(), { wait: 250 });
	useEffect(() => {
		if ((search.q ?? "") !== debouncedText) {
			setSearch({ q: debouncedText || undefined });
		}
	}, [debouncedText, search.q, setSearch]);

	const listInput = useMemo(
		() => listInputFrom(organizationId, search),
		[organizationId, search],
	);
	const deferredInput = useDeferredValue(listInput);
	const { data: list, isFetching } = useSuspenseQuery({
		...orpc.orders.list.queryOptions({ input: deferredInput }),
		refetchInterval: HEARTBEAT_POLL_MS,
	});
	const noOrders = list.orders.length === 0;
	const { data: counts } = useSuspenseQuery({
		...orpc.orders.counts.queryOptions({
			input: {
				organizationId,
				...(search.store ? { storeId: search.store } : {}),
			},
		}),
		refetchInterval: HEARTBEAT_POLL_MS,
	});
	const { loadMore, loadingMore } = useLoadOlderOrders(deferredInput, list);

	const selectedId = search.id ?? null;
	const select = useCallback(
		(id: string | null) => setSearch({ id: id ?? undefined }),
		[setSearch],
	);
	useListKeyboard(list.orders, selectedId, select);
	useNewOrderSound(counts.new);

	const queue = search.queue ?? "new";

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div
				className={cn(
					"flex shrink-0 flex-col gap-3 border-border/70 border-b px-4 pt-4 pb-3 md:px-6",
					selectedId && "max-lg:hidden",
				)}
			>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
					<h1 className="font-semibold text-xl tracking-tight">
						Orders
					</h1>
					<div className="ml-auto flex items-center gap-2">
						<SoundToggle />
						{isAdmin ? (
							<LinkButton
								variant="outline"
								size="sm"
								to="/app/$organizationSlug/orders/setup"
								params={{ organizationSlug }}
							>
								<SettingsIcon data-icon="inline-start" />
								Setup
							</LinkButton>
						) : null}
					</div>
					<div className="flex w-full items-center gap-2 lg:w-auto lg:flex-none">
						<OrderSearch
							value={searchText}
							onChange={setSearchText}
							busy={isFetching}
						/>
						<BranchFilter
							organizationId={organizationId}
							store={search.store}
							onChange={(store) =>
								setSearch({ store, id: undefined })
							}
						/>
					</div>
				</div>
				<QueueTabs
					counts={counts}
					queue={queue}
					onChange={(next) =>
						setSearch({ queue: next, id: undefined })
					}
				/>
			</div>

			<div className="flex min-h-0 flex-1">
				<aside
					aria-label="Orders"
					className={cn(
						"min-h-0 min-w-0 flex-col border-border/70 lg:flex lg:w-[22rem] lg:shrink-0 lg:border-r xl:w-96",
						selectedId ? "hidden" : "flex flex-1 lg:flex-none",
						// An empty queue: one empty state across the page, not one per pane.
						noOrders &&
							"lg:w-auto lg:flex-1 lg:border-r-0 xl:w-auto",
					)}
				>
					<OrderList
						orders={list.orders}
						selectedId={selectedId}
						onSelect={select}
						hasMore={Boolean(list.nextCursor)}
						loadingMore={loadingMore}
						onLoadMore={loadMore}
						queue={queue}
						filtered={Boolean(search.q || search.store)}
						onClearFilters={() => {
							setSearchText("");
							setSearch({ q: undefined, store: undefined });
						}}
					/>
				</aside>

				<section
					aria-label="Order"
					className={cn(
						"min-h-0 min-w-0 flex-1 flex-col lg:flex",
						selectedId ? "flex" : "hidden",
						noOrders && !selectedId && "lg:hidden",
					)}
				>
					{selectedId ? (
						<OrderPane
							key={selectedId}
							orderId={selectedId}
							organizationId={organizationId}
							organizationSlug={organizationSlug}
							isAdmin={isAdmin}
							onBack={() => select(null)}
						/>
					) : (
						<OrderDetailEmpty />
					)}
				</section>
			</div>
		</div>
	);
}

/** Older pages of the list, appended to the cached first page. */
function useLoadOlderOrders(input: ListInput, list: ListData) {
	const queryClient = useQueryClient();
	const [loadingMore, setLoadingMore] = useState(false);
	const loadMore = async () => {
		if (!list.nextCursor || loadingMore) {
			return;
		}
		setLoadingMore(true);
		try {
			const next = await orpcClient.orders.list({
				...input,
				cursor: list.nextCursor,
			});
			queryClient.setQueryData(
				orpc.orders.list.queryKey({ input }),
				(old: ListData | undefined) => {
					if (!old) {
						return next;
					}
					const seen = new Set(old.orders.map((o) => o.id));
					return {
						...next,
						orders: [
							...old.orders,
							...next.orders.filter((o) => !seen.has(o.id)),
						],
					};
				},
			);
		} catch {
			toast.add({
				type: "error",
				title: "Couldn't load older orders",
				description: "Check your connection and try again.",
			});
		} finally {
			setLoadingMore(false);
		}
	};
	return { loadMore, loadingMore };
}

/** ↑/↓ or j/k through the visible rows, as in the Inbox. */
function useListKeyboard(
	orders: readonly OrderRow[],
	selectedId: string | null,
	select: (id: string) => void,
) {
	const onKey = useEffectEvent((event: KeyboardEvent) => {
		const step = rowStep(event);
		if (!step || orders.length === 0) {
			return;
		}
		event.preventDefault();
		const rows = orders.map((o) => o.id);
		const index = selectedId ? rows.indexOf(selectedId) : -1;
		const next = rows[Math.min(rows.length - 1, Math.max(0, index + step))];
		if (next && next !== selectedId) {
			select(next);
			document
				.querySelector(`[data-order-id="${next}"]`)
				?.scrollIntoView({ block: "nearest" });
		}
	});
	useEffect(() => {
		const listener = (event: KeyboardEvent) => onKey(event);
		window.addEventListener("keydown", listener);
		return () => window.removeEventListener("keydown", listener);
	}, []);
}

/** +1 or -1 for a row key pressed outside a field, menu or dialog; 0 for anything else. */
function rowStep(event: KeyboardEvent): number {
	const target = event.target as HTMLElement | null;
	if (
		event.metaKey ||
		event.ctrlKey ||
		event.altKey ||
		target?.closest(
			"input, textarea, select, [contenteditable], [role=dialog], [role=menu]",
		)
	) {
		return 0;
	}
	if (event.key === "ArrowDown" || event.key === "j") {
		return 1;
	}
	return event.key === "ArrowUp" || event.key === "k" ? -1 : 0;
}

function OrderSearch({
	value,
	onChange,
	busy,
}: {
	value: string;
	onChange: (value: string) => void;
	busy: boolean;
}) {
	return (
		<InputGroup className="min-w-0 flex-1 lg:w-64 lg:flex-none">
			<InputGroupAddon>
				{busy ? <Spinner /> : <SearchIcon aria-hidden />}
			</InputGroupAddon>
			<InputGroupInput
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder="Order number, name or phone"
				aria-label="Search orders"
				dir="auto"
			/>
			{value ? (
				<InputGroupAddon align="inline-end">
					<InputGroupButton
						size="icon-xs"
						onClick={() => onChange("")}
						aria-label="Clear search"
					>
						<XIcon />
					</InputGroupButton>
				</InputGroupAddon>
			) : null}
		</InputGroup>
	);
}

/** One branch's orders; shown when there is more than one branch, or one is picked. */
function BranchFilter({
	organizationId,
	store,
	onChange,
}: {
	organizationId: string;
	store: string | undefined;
	onChange: (store: string | undefined) => void;
}) {
	const stores = useQuery(
		orpc.stores.list.queryOptions({ input: { organizationId } }),
	);
	const storeItems: Record<string, string> = {
		[ALL_BRANCHES]: "All branches",
		...Object.fromEntries(
			(stores.data?.stores ?? []).map((s) => [s.id, s.name]),
		),
	};
	if (Object.keys(storeItems).length <= 2 && !store) {
		return null;
	}
	return (
		<Select
			items={storeItems}
			value={store ?? ALL_BRANCHES}
			onValueChange={(next) =>
				onChange(
					!next || next === ALL_BRANCHES ? undefined : String(next),
				)
			}
		>
			<SelectTrigger
				size="sm"
				aria-label="Filter by branch"
				className="w-36 shrink-0 sm:w-44"
			>
				<SelectValue className="truncate" />
			</SelectTrigger>
			<SelectContent>
				{Object.entries(storeItems).map(([id, label]) => (
					<SelectItem key={id} value={id}>
						{label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

/** The open order: its detail, a skeleton while it loads, or why it couldn't open. */
function OrderPane({
	orderId,
	organizationId,
	organizationSlug,
	isAdmin,
	onBack,
}: {
	orderId: string;
	organizationId: string;
	organizationSlug: string;
	isAdmin: boolean;
	onBack: () => void;
}) {
	const detailQuery = useQuery(
		orpc.orders.get.queryOptions({ input: { organizationId, orderId } }),
	);
	if (detailQuery.data) {
		return (
			<OrderDetail
				order={detailQuery.data}
				organizationId={organizationId}
				organizationSlug={organizationSlug}
				isAdmin={isAdmin}
				onBack={onBack}
			/>
		);
	}
	if (detailQuery.isError) {
		return (
			<Empty className="m-auto">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<CircleAlertIcon />
					</EmptyMedia>
					<EmptyTitle>Couldn't open this order</EmptyTitle>
					<EmptyDescription>
						It may belong to another retailer, or the connection
						dropped.
					</EmptyDescription>
				</EmptyHeader>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={onBack}>
						Back to orders
					</Button>
					<Button size="sm" onClick={() => detailQuery.refetch()}>
						Try again
					</Button>
				</div>
			</Empty>
		);
	}
	return <OrderDetailSkeleton />;
}

function QueueTabs({
	counts,
	queue,
	onChange,
}: {
	counts: Record<OrderQueue, number>;
	queue: OrderQueue;
	onChange: (queue: OrderQueue) => void;
}) {
	return (
		<fieldset className="no-scrollbar flex min-w-0 gap-1 overflow-x-auto">
			<legend className="sr-only">Order queue</legend>
			{ORDER_QUEUES.map((value) => {
				const active = queue === value;
				const count = counts[value] ?? 0;
				const urgent = value === "new" && count > 0;
				return (
					<button
						key={value}
						type="button"
						aria-pressed={active}
						onClick={() => onChange(value)}
						className={cn(
							"inline-flex h-7 shrink-0 items-center gap-1.5 rounded-xl px-2.5 font-medium text-sm outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
							active
								? "bg-foreground text-background"
								: urgent
									? "bg-warning/18 text-warning-foreground hover:bg-warning/28 dark:bg-warning/14 dark:text-warning"
									: "text-muted-foreground hover:bg-muted hover:text-foreground",
						)}
					>
						{QUEUE_LABEL[value]}
						{COUNTED_QUEUES.has(value) ? (
							<span
								className={cn(
									"tabular text-xs",
									active
										? "text-background/70"
										: urgent
											? ""
											: "text-muted-foreground",
								)}
							>
								{count.toLocaleString("en-US")}
							</span>
						) : null}
					</button>
				);
			})}
		</fieldset>
	);
}

const EMPTY_COPY: Record<OrderQueue, { title: string; description: string }> = {
	new: {
		title: "No new orders",
		description:
			"Orders shoppers place on WhatsApp or the web land here live.",
	},
	preparing: {
		title: "Nothing being prepared",
		description: "Accepted orders wait here until they leave the branch.",
	},
	on_the_way: {
		title: "Nothing on the way",
		description: "Orders out for delivery show here until they arrive.",
	},
	done: {
		title: "No finished orders yet",
		description: "Delivered and picked up orders are kept here.",
	},
	cancelled: {
		title: "No cancelled orders",
		description:
			"Rejected and cancelled orders are kept here with the reason.",
	},
};

function OrderList({
	orders,
	selectedId,
	onSelect,
	hasMore,
	loadingMore,
	onLoadMore,
	queue,
	filtered,
	onClearFilters,
}: {
	orders: readonly OrderRow[];
	selectedId: string | null;
	onSelect: (id: string) => void;
	hasMore: boolean;
	loadingMore: boolean;
	onLoadMore: () => void;
	queue: OrderQueue;
	filtered: boolean;
	onClearFilters: () => void;
}) {
	if (orders.length === 0) {
		return filtered ? (
			<NoMatches
				title="No orders match"
				clearLabel="Show every branch and order"
				onClear={onClearFilters}
			/>
		) : (
			<Empty className="m-auto">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<PackageIcon />
					</EmptyMedia>
					<EmptyTitle>{EMPTY_COPY[queue].title}</EmptyTitle>
					<EmptyDescription>
						{EMPTY_COPY[queue].description}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		);
	}
	return (
		<ul className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
			{orders.map((order) => (
				<li key={order.id}>
					<OrderRowButton
						order={order}
						selected={order.id === selectedId}
						onSelect={onSelect}
					/>
				</li>
			))}
			{hasMore ? (
				<li className="px-1 pt-2 pb-1">
					<Button
						variant="ghost"
						size="sm"
						onClick={onLoadMore}
						disabled={loadingMore}
						className="w-full text-muted-foreground"
					>
						{loadingMore ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Load older orders
					</Button>
				</li>
			) : null}
		</ul>
	);
}

function OrderRowButton({
	order,
	selected,
	onSelect,
}: {
	order: OrderRow;
	selected: boolean;
	onSelect: (id: string) => void;
}) {
	const isNew = order.status === "PLACED";
	return (
		<button
			type="button"
			data-order-id={order.id}
			aria-current={selected ? "true" : undefined}
			onClick={() => onSelect(order.id)}
			className={cn(
				"flex w-full flex-col gap-1 rounded-2xl px-3 py-2.5 text-left outline-none transition-colors duration-(--duration-hover) focus-visible:ring-3 focus-visible:ring-ring/30",
				selected ? "bg-muted" : "hover:bg-muted/60",
			)}
		>
			<span className="flex items-center gap-2">
				{isNew ? (
					<span className="size-2 shrink-0 rounded-full bg-warning">
						<span className="sr-only">New:</span>
					</span>
				) : null}
				<span className="shrink-0 font-semibold text-sm tabular">
					{orderName(order)}
				</span>
				<span
					dir="auto"
					className={cn(
						"min-w-0 flex-1 truncate text-left text-sm",
						isNew ? "font-medium" : "",
					)}
				>
					{order.customer?.name ?? "Shopper"}
				</span>
				<span className="shrink-0 font-medium text-sm tabular">
					{money(order.total, order.currency)}
				</span>
			</span>
			<span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-xs">
				<OrderRowStatus order={order} isNew={isNew} />
				<OrderRowPlace order={order} />
				{order.slotStart ? (
					<Time
						at={new Date(order.slotStart)}
						label="Slot"
						className="tabular"
					/>
				) : (
					<span>ASAP</span>
				)}
				<span className="tabular">
					{order.itemCount} item{order.itemCount === 1 ? "" : "s"}
				</span>
				{order.paymentMethod ? (
					<span>
						{PAYMENT_LABEL[order.paymentMethod] ??
							order.paymentMethod}
					</span>
				) : null}
				<WaitingFor order={order} className="ml-auto" />
			</span>
		</button>
	);
}

/** An address to confirm first; otherwise the status, which a new order's dot already says. */
function OrderRowStatus({ order, isNew }: { order: OrderRow; isNew: boolean }) {
	if (order.addressNeedsConfirmation && order.fulfilment !== "PICKUP") {
		return (
			<Badge variant="warning">
				<MapPinIcon aria-hidden />
				Confirm address
			</Badge>
		);
	}
	if (isNew) {
		return null;
	}
	const status = STATUS_META[order.status];
	return (
		<Badge variant={status.variant}>
			{order.statusLabel ?? status.label}
		</Badge>
	);
}

function OrderRowPlace({ order }: { order: OrderRow }) {
	if (order.fulfilment === "PICKUP") {
		return <span>Pickup</span>;
	}
	return order.area ? (
		<span dir="auto" className="max-w-40 truncate">
			{order.area}
		</span>
	) : null;
}

/**
 * Opt-in per browser (spec 5.3): a short chime when the New count goes up while the screen is
 * open. Browsers only play sound after the person interacted with the page, which turning it on
 * is.
 */
function useNewOrderSound(newCount: number) {
	const previous = useRef(newCount);
	useEffect(() => {
		const rose = newCount > previous.current;
		previous.current = newCount;
		if (!rose || !soundEnabled()) {
			return;
		}
		chime();
	}, [newCount]);
}

function soundEnabled(): boolean {
	try {
		return window.localStorage.getItem(SOUND_KEY) === "on";
	} catch {
		return false;
	}
}

function chime() {
	try {
		const context = new AudioContext();
		const gain = context.createGain();
		gain.connect(context.destination);
		gain.gain.setValueAtTime(0.0001, context.currentTime);
		gain.gain.exponentialRampToValueAtTime(0.2, context.currentTime + 0.02);
		gain.gain.exponentialRampToValueAtTime(
			0.0001,
			context.currentTime + 0.6,
		);
		for (const [index, frequency] of [880, 1320].entries()) {
			const tone = context.createOscillator();
			tone.frequency.value = frequency;
			tone.connect(gain);
			tone.start(context.currentTime + index * 0.12);
			tone.stop(context.currentTime + 0.6);
		}
		window.setTimeout(() => void context.close(), 1000);
	} catch {
		// No audio device or blocked by the browser: the badge still says it.
	}
}

function SoundToggle() {
	const [on, setOn] = useState(false);
	useEffect(() => setOn(soundEnabled()), []);
	const toggle = () => {
		const next = !on;
		try {
			window.localStorage.setItem(SOUND_KEY, next ? "on" : "off");
		} catch {
			// Private mode: the choice lasts for this page only.
		}
		setOn(next);
		if (next) {
			chime();
		}
		toast.add({
			type: "info",
			title: next
				? "Sound on for new orders in this browser"
				: "Sound off for new orders",
		});
	};
	const label = on
		? "Turn off the new order sound"
		: "Play a sound for new orders";
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={toggle}
						aria-label={label}
						aria-pressed={on}
					/>
				}
			>
				{on ? <BellIcon /> : <BellOffIcon />}
			</TooltipTrigger>
			<TooltipContent>{label}</TooltipContent>
		</Tooltip>
	);
}

export function OrdersSkeleton() {
	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex flex-col gap-3 border-border/70 border-b px-4 pt-4 pb-3 md:px-6">
				<div className="flex items-center justify-between gap-3">
					<Skeleton className="h-7 w-24" />
					<Skeleton className="h-8 w-64 max-sm:hidden" />
				</div>
				<div className="flex gap-1">
					{ORDER_QUEUES.map((queue) => (
						<Skeleton
							key={queue}
							className="h-7"
							style={{ width: QUEUE_TAB_WIDTH[queue] }}
						/>
					))}
				</div>
			</div>
			<div className="flex min-h-0 flex-1">
				<div className="flex w-full flex-col gap-1 p-2 lg:w-[22rem] lg:border-border/70 lg:border-r xl:w-96">
					{Array.from({ length: 6 }).map((_, i) => (
						<div
							key={i}
							className="flex flex-col gap-2 px-3 py-2.5"
						>
							<div className="flex justify-between gap-3">
								<Skeleton className="h-4 w-1/2" />
								<Skeleton className="h-4 w-16" />
							</div>
							<Skeleton className="h-3 w-3/4" />
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
